/**
 * Text size — a per-browser zoom of the whole application (14-theme.md §5.4).
 *
 * The app is set at a 13 px data-tool density (§5.1), with 10 px field labels and table
 * headers, and office users said it was too small to read (8 Oct 2026). This is the setting
 * they asked for: four steps from the user menu (13-ui-screens.md §4.2), remembered by the
 * browser and applied before the first render, the sign-in screen included.
 *
 * ── Why `zoom`, not bigger font tokens ──────────────────────────────────────
 * Raising `--cm-text-*` and antd's `fontSize` would have grown body text and left almost
 * everything else where it was: the SPA carries ~670 inline `fontSize: <n>` literals, and they
 * are precisely the 10–12 px labels, headers and figures people struggle with. Growing text
 * inside fixed 34 px controls and a 19 px tick box would also have clipped it. CSS `zoom` on
 * `<html>` scales text, padding, control heights and borders together, so every screen keeps
 * the proportions it was designed at — it is what Ctrl + does, put where the office can find
 * it (and it compounds with whatever the browser is already zoomed to). tokens.css applies it
 * from `--cm-zoom`, which is the ONE thing this module writes, and resets it to 1 for print so
 * a payslip still fits A4.
 *
 * ── Why per browser, not per user ───────────────────────────────────────────
 * `localStorage`, not a column on `app_user`. Text size belongs to a person at a particular
 * screen — the same clerk wants more on the shop-floor monitor than on a laptop — and it has
 * to work on the sign-in screen, before anybody is known. It moves no money and nothing on the
 * server reads it, so it is not audited; if the browser loses it (a private window, cleared
 * site data) the page simply renders at the default and one click restores it. Every storage
 * access is wrapped, because some browsers throw on it rather than return null.
 *
 * ── What zoom changes for code that measures ────────────────────────────────
 * Under standardised zoom (Chrome / Edge 128+, Firefox 126+) `getBoundingClientRect()` and a
 * mouse event's `clientX` / `clientY` are VISUAL pixels, while a `left` / `top` written onto an
 * element inside `<html>` is a CSS pixel and gets multiplied by the zoom again — a tooltip
 * placed from a measured rect lands at 1.25 × 1.25 the distance. `pageZoom()` is the divisor;
 * `HoverTip` is the one place that positions from a rect. antd's popups need nothing: its
 * trigger measures its own scale. `100vh` is the other trap — it resolves to the viewport
 * TIMES the zoom — so the shell sizes itself with `var(--cm-viewport-h)` instead.
 */
import { useSyncExternalStore } from 'react';

export const TEXT_SIZES = [
  { value: 100, label: 'Default' },
  { value: 110, label: 'Large' },
  { value: 125, label: 'Larger' },
  { value: 140, label: 'Largest' },
] as const satisfies readonly { value: number; label: string }[];

/** A percentage, and only one of the four steps — a stored `117` from nowhere is not one. */
export type TextSize = (typeof TEXT_SIZES)[number]['value'];

export const DEFAULT_TEXT_SIZE: TextSize = 100;

/** The `localStorage` key. Prefixed so it cannot collide with another app on the same origin. */
export const TEXT_SIZE_STORAGE_KEY = 'cm.textSize';

/** Stored text → a step. Anything that is not exactly one of the four is the default. */
export function parseTextSize(raw: string | null | undefined): TextSize {
  const n = Number(raw);
  return TEXT_SIZES.find((s) => s.value === n)?.value ?? DEFAULT_TEXT_SIZE;
}

/** `Larger` — the step's name, for the menu's caption. */
export function textSizeLabel(size: TextSize): string {
  return TEXT_SIZES.find((s) => s.value === size)?.label ?? 'Default';
}

let current: TextSize = DEFAULT_TEXT_SIZE;
const listeners = new Set<() => void>();

function apply(size: TextSize): void {
  const changed = size !== current;
  current = size;
  const root = document.documentElement.style;
  /* At the default the property is REMOVED rather than set to 1, so tokens.css's own
   * `--cm-zoom: 1` is what is in force and a fresh install carries no inline style at all. */
  if (size === DEFAULT_TEXT_SIZE) root.removeProperty('--cm-zoom');
  else root.setProperty('--cm-zoom', String(size / 100));
  if (!changed) return;
  for (const notify of listeners) notify();
  /* The effective viewport just narrowed or widened in CSS pixels, but the window did not
   * resize, so nothing that lays out on `resize` knows: an open antd popup (the user menu
   * this was chosen from) would keep its old offset, and a table measuring its scroll width
   * would keep the old one. One synthetic event tells them all. */
  window.dispatchEvent(new Event('resize'));
}

function readStored(): string | null {
  try {
    return window.localStorage.getItem(TEXT_SIZE_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Applies the remembered size. `main.tsx` calls it before the first render, so a user on
 * `Largest` never sees a frame at 100 %. Also follows a change made in another tab of the same
 * browser — the `storage` event fires only in the OTHER tabs, which is exactly the set that
 * needs telling.
 */
export function initTextSize(): void {
  apply(parseTextSize(readStored()));
  window.addEventListener('storage', (e) => {
    if (e.key === TEXT_SIZE_STORAGE_KEY) apply(parseTextSize(e.newValue));
  });
}

export function setTextSize(size: TextSize): void {
  try {
    if (size === DEFAULT_TEXT_SIZE) window.localStorage.removeItem(TEXT_SIZE_STORAGE_KEY);
    else window.localStorage.setItem(TEXT_SIZE_STORAGE_KEY, String(size));
  } catch {
    /* Storage refused: the size still applies to this tab; it just will not be remembered. */
  }
  apply(size);
}

/**
 * The factor between a measured (visual) pixel and a CSS pixel inside `<html>` — 1.25 on
 * `Larger`. Divide a `getBoundingClientRect()` value or `window.innerWidth` by it before
 * writing it to a `position: fixed` element's `left` / `top`.
 */
export function pageZoom(): number {
  return current / 100;
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

/** The current step, re-rendering when it changes — here or in another tab. */
export function useTextSize(): TextSize {
  return useSyncExternalStore(subscribe, () => current, () => DEFAULT_TEXT_SIZE);
}
